import React from 'react';
import './LoadingStates.css';

/**
 * Optimistic loading component that shows different states based on cache status
 */
export const OptimisticLoader = ({ 
  loading, 
  error, 
  hasData, 
  children, 
  loadingText = 'Loading...', 
  errorText = 'Failed to load data',
  onRetry,
  showSkeletonOnEmpty = false,
  skeletonComponent = null
}) => {
  // Show error state
  if (error) {
    return (
      <div className="optimistic-loader-error">
        <div className="optimistic-loader-error-text">{errorText}</div>
        {onRetry && (
          <button 
            onClick={onRetry}
            className="optimistic-loader-retry-button"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // Show loading state only if no cached data is available
  if (loading && !hasData) {
    if (skeletonComponent) {
      return skeletonComponent;
    }
    
    return (
      <div className="optimistic-loader-loading">
        <div className="optimistic-loader-spinner"></div>
        <div className="optimistic-loader-text">{loadingText}</div>
      </div>
    );
  }

  // Show skeleton while loading with no data
  if (!hasData && showSkeletonOnEmpty) {
    if (skeletonComponent) {
      return skeletonComponent;
    }
    
    return (
      <div className="optimistic-loader-skeleton">
        <div className="skeleton-line skeleton-line-title"></div>
        <div className="skeleton-line skeleton-line-medium"></div>
        <div className="skeleton-line skeleton-line-short"></div>
      </div>
    );
  }

  // Show children (data is available or loading with cached data)
  return (
    <div className={`optimistic-loader-content ${loading ? 'optimistic-loader-refreshing' : ''}`}>
      {loading && hasData && (
        <div className="optimistic-loader-refresh-indicator">
          <div className="optimistic-loader-refresh-spinner"></div>
        </div>
      )}
      {children}
    </div>
  );
};

/**
 * Skeleton components for different data types
 */
export const SymbolsTableSkeleton = () => (
  <div className="symbols-table-skeleton">
    <div className="skeleton-table-header">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="skeleton-header-cell"></div>
      ))}
    </div>
    <div className="skeleton-table-body">
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} className="skeleton-table-row">
          {Array.from({ length: 8 }).map((_, j) => (
            <div key={j} className="skeleton-table-cell"></div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const MemoryLayoutSkeleton = () => (
  <div className="memory-layout-skeleton">
    <div className="skeleton-line skeleton-line-title"></div>
    <div className="skeleton-memory-sections">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="skeleton-memory-section">
          <div className="skeleton-section-header"></div>
          <div className="skeleton-section-content"></div>
        </div>
      ))}
    </div>
  </div>
);

export const BranchSelectorSkeleton = () => (
  <div className="branch-selector-skeleton">
    <div className="skeleton-dropdown"></div>
  </div>
);

/**
 * Global loading indicator for background operations
 */
export const GlobalLoadingIndicator = ({ isVisible }) => {
  if (!isVisible) return null;
  
  return (
    <div className="global-loading-indicator">
      <div className="global-loading-bar"></div>
    </div>
  );
};